import { ChangeDetectionStrategy, Component, EventEmitter, inject, Input, OnInit, Output } from '@angular/core';

import { Observable, distinctUntilChanged, switchMap } from 'rxjs';
import { Store } from '@ngrx/store';

import { SvgLibraryIcon } from '@finnairoyj/fcom-ui-styles';

import { ButtonTheme, TagTheme, ToggleSwitchStatus, IconPosition, LinkSize } from '@fcom/ui-components';
import { Category, FinnairLowestPrice } from '@fcom/dapi/api/models';
import { getIconForServiceCategory } from '@fcom/dapi/utils';
import { Amount, ServiceCatalogService } from '@fcom/dapi/interfaces';

import { bundleProductInfoSelector } from '../../../../../store/selectors';

@Component({
  selector: 'fin-bundle-promotion-card',
  templateUrl: './bundle-promotion-card.component.html',
  styleUrls: ['./bundle-promotion-card.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
  standalone: false,
})
export class BundlePromotionCardComponent implements OnInit {
  private store$ = inject(Store);

  readonly SvgLibraryIcon = SvgLibraryIcon;
  readonly IconPosition = IconPosition;
  readonly LinkSize = LinkSize;
  readonly TagTheme = TagTheme;
  readonly ToggleSwitchStatus = ToggleSwitchStatus;
  readonly ButtonTheme = ButtonTheme;

  @Input({ required: true })
  active: boolean;

  @Input({ required: true })
  category$: Observable<Category>;

  @Input({ required: true })
  subCategoryForTranslations$: Observable<string | undefined>;

  @Input({ required: true })
  mode: 'selected' | 'offers';

  @Input()
  bundleUpsellPrice: Amount | undefined;

  @Input()
  removeOnlyAncillary = false;

  @Input()
  lowestPrice: FinnairLowestPrice | null = null;

  @Output()
  toggled: EventEmitter<boolean> = new EventEmitter();

  openModal = false;

  /**
   * The standalone ancillary service shown in the product-info modal, resolved from this card's bundle
   * category. Derived from `category$` so it always reflects the current category: the seatmap reuses
   * the same card instance across category switches (e.g. SEAT_MEAL → SEAT_PRIORITY), so it must react rather
   * than snapshot.
   */
  serviceInfo$: Observable<ServiceCatalogService | null>;

  ngOnInit(): void {
    this.serviceInfo$ = this.category$.pipe(
      distinctUntilChanged(),
      switchMap((category) => this.store$.select(bundleProductInfoSelector(category)))
    );
  }

  emitToggle(value: boolean): void {
    this.toggled.emit(value);
  }

  protected readonly getIconForServiceCategory = getIconForServiceCategory;
  protected readonly Category = Category;
}
